import { BAOCustomElement } from '@bao/custom-element'
import { sectionRenderer } from '@bao/section-rendering'
import { getElement, translateString } from '@bao/utils'

const SWISH_READY_EVENT = 'swish-ready'
const SWISH_READY_TIMEOUT = 10000

const DRAWER_KEY = 'wishlist'
const TOAST_DRAWER_KEY = 'wishlist-toast'

const PRODUCT_CARD_SECTION = 'product-card'
const PRODUCT_CARD_SELECTOR = 'product-card'

const SAVED_FROM_CART_LIST = 'saved-from-cart'

const VIEWS = {
  HOME: 'home',
  SAVES: 'saves',
  LISTS: 'lists',
  LIST: 'list',
}

const SWISH_EVENTS = [
  'item-create',
  'item-update',
  'item-delete',
  'item-lists-update',
  'list-create',
  'list-update',
  'list-delete',
]

const EXTERNAL_SELECTORS = {
  TRIGGER: '[data-swish-wishlist-external-el="trigger"]',
}

const TOAST_EVENTS = {
  ADD_TO_LIST: 'wishlist-toast:add-to-list',
  CREATE_LIST: 'wishlist-toast:create-list',
  SAVE_LISTS: 'wishlist-toast:save-lists',
  SAVE_CREATE: 'wishlist-toast:save-create',
  VIEW: 'wishlist-toast:view',
  RETURN_TO_WISHLIST: 'wishlist-toast:return-to-wishlist',
  COMPLETE: 'wishlist-toast:complete',
}

let swishPromise

class SwishWishlist extends BAOCustomElement() {
  static get requiredElements () {
    return [
      'body',
      'homeHeader',
      'subviewHeader',
      'home',
      'savedFromCartSection',
      'savedFromCart',
      'homeSaves',
      'homeLists',
      'homeListsViewAll',
      'savedCount',
      'saves',
      'savesContent',
      'lists',
      'listsContent',
      'list',
      'listTitle',
      'listCount',
      'listContent',
      'listTemplate',
    ]
  }

  constructor () {
    super()

    this.activeView = VIEWS.HOME
    this.activeList = null
    this.pendingDrawerView = null

    this.wishlistTriggerStates = new Map()
    this.productCardCache = new Map()

    this.unsubscribeSwishEvents = null
    this.disposeCountEffect = null
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))
    this.listeners.add(
      document,
      'click',
      this.handleWishlistTriggerClick.bind(this),
    )
    this.listeners.add(
      document,
      'drawer-changed',
      this.handleDrawerChanged.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.ADD_TO_LIST,
      this.handleToastAddToList.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.CREATE_LIST,
      this.handleToastCreateList.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.SAVE_LISTS,
      this.handleToastSaveLists.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.SAVE_CREATE,
      this.handleToastSaveCreate.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.VIEW,
      this.handleToastView.bind(this),
    )
    this.listeners.add(
      document,
      TOAST_EVENTS.COMPLETE,
      this.handleToastComplete.bind(this),
    )

    this.listeners.add(
      document,
      TOAST_EVENTS.RETURN_TO_WISHLIST,
      this.handleToastReturnToWishlist.bind(this),
    )

    this.initialise()
  }

  disconnectedCallback () {
    this.unsubscribeSwishEvents?.()
    this.disposeCountEffect?.()

    this.wishlistTriggerStates.forEach(({ state, disposeEffect }) => {
      disposeEffect()
      state.dispose()
    })

    this.wishlistTriggerStates.clear()

    super.disconnectedCallback()
  }

  async initialise () {
    try {
      this.siteDrawers = getElement('site-drawers')
      this.swish = await this.getSwish()

      this.setupWishlistTriggers()
      this.setupWishlistCount()
      this.setupSwishEventListeners()

      this.dataset.ready = 'true'

      if (this.drawerIsOpen) {
        await this.showView(VIEWS.HOME)
      }
    } catch (error) {
      console.error('Swish Wishlist: Initialisation failed.', error)

      this.dataset.error = 'true'
      this.setLoading(false)
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Drawer
  |--------------------------------------------------------------------------
  |
  */
  handleDrawerChanged () {
    if (this.swish) {
      this.setupWishlistTriggers(
        document.querySelectorAll(EXTERNAL_SELECTORS.TRIGGER),
      )
    }

    if (!this.drawerIsOpen) {
      this.setLoading(false)

      return
    }

    if (!this.swish) {
      this.setLoading(true)

      return
    }

    const view = this.pendingDrawerView || VIEWS.HOME

    this.pendingDrawerView = null
    this.showView(view)
  }

  get drawerIsOpen () {
    return window.drawers?.activeDrawerKey === DRAWER_KEY
  }

  /*
  |--------------------------------------------------------------------------
  | Views
  |--------------------------------------------------------------------------
  |
  */
  handleClick (event) {
    const backTrigger = event.target.closest('[data-swish-wishlist-back]')

    if (backTrigger) {
      this.handleBack()

      return
    }

    const createListTrigger = event.target.closest(
      '[data-swish-wishlist-create-list]',
    )

    if (createListTrigger) {
      this.handleCreateList()

      return
    }

    const listActionTrigger = event.target.closest('[data-swish-list-action]')

    if (listActionTrigger) {
      event.preventDefault()

      this.handleListAction(listActionTrigger)

      return
    }

    const listTrigger = event.target.closest('[data-swish-wishlist-list-id]')

    if (listTrigger) {
      event.preventDefault()

      this.handleListClick(listTrigger)

      return
    }

    const viewTrigger = event.target.closest('[data-swish-wishlist-view]')

    if (!viewTrigger) return

    this.showView(viewTrigger.dataset.swishWishlistView)
  }

  handleCreateList () {
    const toast = this.toast

    if (!toast) return

    toast.showCreate({
      standalone: true,
    })

    this.siteDrawers.open(TOAST_DRAWER_KEY)
  }

  handleBack () {
    if (this.activeView === VIEWS.LIST) {
      this.showView(VIEWS.LISTS)

      return
    }

    this.showView(VIEWS.HOME)
  }

  handleListClick (trigger) {
    this.activeList = {
      id: trigger.dataset.swishWishlistListId,
      name: trigger.dataset.swishWishlistListName,
      itemCount: Number(trigger.dataset.swishWishlistListCount || 0),
    }

    this.updateListView()
    this.showView(VIEWS.LIST)
  }

  async showView (view) {
    if (!this.swish || !Object.values(VIEWS).includes(view)) return

    const viewChanged = view !== this.activeView

    this.activeView = view

    this.updateViewHeaders()
    this.updateViewPanels()
    this.updateViewTriggers()
    this.setLoading(true)

    try {
      await this.loadView(view)

      if (viewChanged) {
        this.scrollViewToTop(view)
      }
    } catch (error) {
      console.error(`Swish Wishlist: Failed to load the ${view} view.`, error)

      this.renderViewError(view)
    } finally {
      this.setLoading(false)
    }
  }

  scrollViewToTop (view) {
    const panel = this.els[view]?.element

    if (!panel) return

    panel.scrollIntoView({
      behavior: 'auto',
      block: 'start',
    })
  }

  updateViewHeaders () {
    const isHome = this.activeView === VIEWS.HOME

    this.els.homeHeader.element.hidden = !isHome
    this.els.subviewHeader.element.hidden = isHome
  }

  updateViewPanels () {
    const panels = {
      [VIEWS.HOME]: this.els.home.element,
      [VIEWS.SAVES]: this.els.saves.element,
      [VIEWS.LISTS]: this.els.lists.element,
      [VIEWS.LIST]: this.els.list.element,
    }

    Object.entries(panels).forEach(([view, panel]) => {
      panel.hidden = view !== this.activeView
    })
  }

  updateViewTriggers () {
    const triggers = this.querySelectorAll('[data-swish-wishlist-view]')
    const activeToolbarView =
      this.activeView === VIEWS.LIST ? VIEWS.LISTS : this.activeView

    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.swishWishlistView === activeToolbarView

      trigger.dataset.active = String(isActive)

      if (isActive) {
        trigger.setAttribute('aria-current', 'page')
      } else {
        trigger.removeAttribute('aria-current')
      }
    })
  }

  updateListView () {
    if (!this.activeList) return

    const { name, itemCount } = this.activeList
    const countTranslation =
      itemCount === 1
        ? this.strings.savedCountOne
        : this.strings.savedCountOther

    this.els.listTitle.element.textContent = name
    this.els.listCount.element.textContent = translateString(countTranslation, {
      count: itemCount,
    })
  }

  async loadView (view) {
    if (view === VIEWS.HOME) {
      this.els.homeListsViewAll.element.hidden = true

      const [, , lists] = await Promise.all([
        this.loadSavedFromCart(),
        this.loadSavedItems(this.els.homeSaves.element, {
          limit: 6,
          carousel: true,
        }),
        this.loadLists(this.els.homeLists.element, {
          limit: 4,
          renderLimit: 3,
        }),
      ])

      this.els.homeListsViewAll.element.hidden = lists.length <= 3

      return
    }

    if (view === VIEWS.SAVES) {
      await this.loadSavedItems(this.els.savesContent.element, {
        limit: 24,
      })

      return
    }

    if (view === VIEWS.LISTS) {
      await this.loadLists(this.els.listsContent.element, {
        limit: 50,
        allowDelete: true,
      })

      return
    }

    if (view === VIEWS.LIST && this.activeList?.id) {
      await this.loadSavedItems(this.els.listContent.element, {
        limit: 24,
        listId: this.activeList.id,
      })
    }
  }

  setLoading (isLoading) {
    this.setAttribute('aria-busy', String(isLoading))
  }

  /*
  |--------------------------------------------------------------------------
  | Wishlist triggers
  |--------------------------------------------------------------------------
  |
  */
  setupWishlistTriggers (triggers = this.getExternalElements('trigger')) {
    Array.from(triggers).forEach((trigger) => {
      if (trigger.dataset.swishWishlistAction === 'save-from-cart') return

      this.setupWishlistTrigger(trigger)
    })
  }

  setupWishlistTriggersWithin (container) {
    if (!container) return

    this.setupWishlistTriggers(
      container.querySelectorAll(EXTERNAL_SELECTORS.TRIGGER),
    )
  }

  destroyWishlistTriggersWithin (container) {
    if (!container) return

    const triggers = container.querySelectorAll(EXTERNAL_SELECTORS.TRIGGER)

    triggers.forEach((trigger) => {
      const triggerState = this.wishlistTriggerStates.get(trigger)

      if (!triggerState) return

      triggerState.disposeEffect()
      triggerState.state.dispose()

      this.wishlistTriggerStates.delete(trigger)
    })
  }

  setupWishlistTrigger (trigger) {
    if (this.wishlistTriggerStates.has(trigger)) return

    if (!trigger.dataset.productId) {
      console.error(
        'Swish Wishlist: A wishlist trigger is missing a product ID.',
        trigger,
      )

      trigger.dataset.error = 'true'

      return
    }

    const context = this.swish.state.itemContext(trigger)

    const state = this.swish.state.itemState(context, {
      match: trigger.dataset.swishWishlistMatch || 'auto',
      onSave: (data) => this.handleWishlistSaved(trigger, data),
    })

    const disposeEffect = this.swish.state.effect(() => {
      this.renderWishlistTrigger(trigger, state.value)
    })

    this.wishlistTriggerStates.set(trigger, {
      state,
      disposeEffect,
    })
  }

  async handleWishlistTriggerClick (event) {
    const trigger = event.target.closest(EXTERNAL_SELECTORS.TRIGGER)

    if (!trigger || !this.swish) return

    const action = trigger.dataset.swishWishlistAction || 'toggle'

    event.preventDefault()
    event.stopPropagation()

    try {
      if (action === 'save-from-cart') {
        await this.saveFromCart(trigger)

        return
      }

      let triggerState = this.wishlistTriggerStates.get(trigger)

      if (!triggerState) {
        this.setupWishlistTrigger(trigger)

        triggerState = this.wishlistTriggerStates.get(trigger)
      }

      if (!triggerState || triggerState.state.value.loading) return

      await triggerState.state.toggle()
    } catch (error) {
      console.error('Swish Wishlist: Unable to update the saved item.', error)
    }
  }

  async saveFromCart (trigger) {
    const productId = Number(trigger.dataset.productId)
    const variantId = Number(trigger.dataset.variantId)
    const quantity = Number(trigger.dataset.quantity || 1)

    if (!productId || !variantId) {
      throw new Error(
        'Product or variant ID is missing from the cart save trigger.',
      )
    }

    trigger.disabled = true
    trigger.dataset.loading = 'true'

    try {
      const activity = await this.swish.intents.invoke('save:cart-line', {
        items: [
          {
            productId,
            variantId,
            quantity,
          },
        ],
      })

      const result = await activity.complete

      if (result?.code && result.code !== 'ok') {
        throw new Error(
          `Swish Save From Cart failed with code: ${result.code}`,
        )
      }
    } finally {
      if (trigger.isConnected) {
        trigger.disabled = false
        trigger.dataset.loading = 'false'
      }
    }
  }

  renderWishlistTrigger (trigger, state) {
    const label = trigger.querySelector('[data-swish-wishlist-label]')

    const labelText = state.saved
      ? trigger.dataset.swishWishlistRemoveLabel || this.strings.remove
      : trigger.dataset.swishWishlistSaveLabel || this.strings.save

    trigger.dataset.state = state.status
    trigger.dataset.saved = String(state.saved)
    trigger.dataset.loading = String(state.loading)

    trigger.toggleAttribute('data-error', Boolean(state.error))

    if (state.savedItemId) {
      trigger.dataset.swishItemId = state.savedItemId
    } else {
      delete trigger.dataset.swishItemId
    }

    trigger.disabled = state.loading

    trigger.setAttribute('aria-pressed', String(state.saved))
    trigger.setAttribute('aria-label', labelText)

    if (label) {
      label.textContent = labelText
    }
  }

  handleWishlistSaved (trigger, data) {
    const toast = this.toast
    const itemId = data?.item?.id

    if (!toast || !itemId) return

    const title = data?.product?.title || trigger.dataset.productTitle || ''

    const image =
      data?.variant?.image?.url ||
      data?.product?.featuredImage?.url ||
      data?.product?.images?.nodes?.[0]?.url ||
      trigger.dataset.productImage ||
      ''

    toast.showSaved({
      itemId,
      title,
      image,
    })

    this.siteDrawers.open(TOAST_DRAWER_KEY)
  }

  /*
  |--------------------------------------------------------------------------
  | Wishlist toast
  |--------------------------------------------------------------------------
  |
  */
  async handleToastAddToList (event) {
    const toast = this.toast
    const itemId = event.detail?.itemId

    if (!toast || !itemId) return

    toast.setLoading(true)

    try {
      const [listsResponse, itemResponse] = await Promise.all([
        this.swish.api.lists.list({
          limit: 50,
          sort: 'recently_updated',
        }),
        this.swish.api.items.findById(itemId),
      ])

      const lists = this.getResponseData(
        listsResponse,
        'Unable to load wishlist lists.',
      )

      const item = this.getResponseData(
        itemResponse,
        'Unable to load the saved item.',
      )

      if (lists.length === 0) {
        toast.showCreate()

        return
      }

      toast.showLists({
        lists,
        selectedListIds: item?.listIds || [],
      })
    } catch (error) {
      console.error(
        'Swish Wishlist: Unable to load lists for the toast.',
        error,
      )

      toast.showError(this.strings.toastError)
    } finally {
      toast.setLoading(false)
    }
  }

  handleToastCreateList () {
    this.toast?.showCreate()
  }

  handleToastReturnToWishlist () {
    this.pendingDrawerView = VIEWS.HOME
    this.siteDrawers.open(DRAWER_KEY)
  }

  handleToastView (event) {
    const lists = event.detail?.lists || []
    const list = lists.length === 1 ? lists[0] : null

    if (list?.id) {
      this.activeList = {
        id: list.id,
        name: list.name,
        itemCount: Number(list.itemCount || 0),
      }

      this.updateListView()
      this.pendingDrawerView = VIEWS.LIST
    } else {
      this.activeList = null
      this.pendingDrawerView = VIEWS.LISTS
    }

    this.siteDrawers.open(DRAWER_KEY)
  }

  handleToastComplete () {
    const toastDrawer = this.toast?.closest('[is="site-drawer"]')

    if (!toastDrawer?.open) return

    this.siteDrawers.close()
  }

  async handleToastSaveLists (event) {
    const toast = this.toast
    const itemId = event.detail?.itemId
    const selectedLists = event.detail?.lists || []

    if (!toast || !itemId || selectedLists.length === 0) return

    toast.setLoading(true)

    try {
      const itemResponse = await this.swish.api.items.findById(itemId)
      const item = this.getResponseData(
        itemResponse,
        'Unable to load the saved item.',
      )

      const currentListIds = item?.listIds || []
      const listsToAdd = selectedLists.filter(
        (list) => !currentListIds.includes(list.id),
      )

      await Promise.all(
        listsToAdd.map(async (list) => {
          const response = await this.swish.api.lists.addItemsToList(list.id, [
            itemId,
          ])

          this.getResponseData(
            response,
            `Unable to add the saved item to ${list.name}.`,
          )
        }),
      )

      toast.showAdded({
        lists: selectedLists.map((list) => {
          return {
            ...list,
            itemCount: Number(list.itemCount || 0) + 1,
          }
        }),
      })
    } catch (error) {
      console.error('Swish Wishlist: Unable to add the item to a list.', error)

      toast.showError(this.strings.toastError)
    } finally {
      toast.setLoading(false)
    }
  }

  async handleToastSaveCreate (event) {
    const toast = this.toast
    const itemId = event.detail?.itemId
    const name = event.detail?.name

    if (!toast || !name) return

    toast.setLoading(true)

    try {
      const createResponse = await this.swish.api.lists.create({
        name,
      })

      const list = this.getResponseData(
        createResponse,
        'Unable to create the wishlist list.',
      )

      if (!itemId) {
        this.activeList = {
          id: list.id,
          name: list.name,
          itemCount: Number(list.itemCount || 0),
        }

        this.updateListView()

        this.pendingDrawerView = VIEWS.LIST
        this.siteDrawers.open(DRAWER_KEY)

        return
      }

      const addResponse = await this.swish.api.lists.addItemsToList(list.id, [
        itemId,
      ])

      this.getResponseData(
        addResponse,
        'Unable to add the saved item to the new list.',
      )

      toast.showAdded({
        lists: [
          {
            id: list.id,
            name: list.name,
            itemCount: Number(list.itemCount || 0) + 1,
          },
        ],
      })
    } catch (error) {
      console.error('Swish Wishlist: Unable to create the list.', error)

      toast.showError(this.strings.toastError)
    } finally {
      toast.setLoading(false)
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Wishlist count
  |--------------------------------------------------------------------------
  |
  */
  setupWishlistCount () {
    const countElements = this.getExternalElements('count')
    const savedCountElements = this.els.savedCount.elements
    const countState = this.swish.state.itemCount()

    this.disposeCountEffect = this.swish.state.effect(() => {
      const { count, loading, error } = countState.value

      countElements.forEach((countElement) => {
        countElement.textContent = loading ? '' : String(count)
        countElement.hidden = loading || count === 0
        countElement.dataset.loading = String(loading)
        countElement.dataset.count = String(count)
        countElement.toggleAttribute('data-error', Boolean(error))
      })

      savedCountElements.forEach((savedCountElement) => {
        const countTranslation =
          count === 1
            ? this.strings.savedCountOne
            : this.strings.savedCountOther

        savedCountElement.textContent = loading
          ? ''
          : translateString(countTranslation, { count })

        savedCountElement.toggleAttribute('data-error', Boolean(error))
      })
    })
  }

  /*
  |--------------------------------------------------------------------------
  | Swish events
  |--------------------------------------------------------------------------
  |
  */
  setupSwishEventListeners () {
    this.unsubscribeSwishEvents = this.swish.events.subscribe(
      SWISH_EVENTS,
      this.handleSwishChanged.bind(this),
    )
  }

  handleSwishChanged () {
    if (!this.drawerIsOpen) return

    this.showView(this.activeView)
  }

  /*
  |--------------------------------------------------------------------------
  | Saved from cart
  |--------------------------------------------------------------------------
  |
  */
  async loadSavedFromCart () {
    const response = await this.swish.api.smartLists.findItems(
      SAVED_FROM_CART_LIST,
      {
        limit: 6,
      },
    )

    const items = this.getResponseData(
      response,
      'Unable to load items saved from cart.',
    )

    const hasItems = await this.renderSavedItemRecords(
      this.els.savedFromCart.element,
      items,
      {
        carousel: true,
        showEmptyState: false,
      },
    )

    this.els.savedFromCartSection.element.hidden = !hasItems

    if (hasItems) {
      this.refreshCarouselsWithin(this.els.savedFromCart.element)
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Saved items
  |--------------------------------------------------------------------------
  |
  */
  async loadSavedItems (container, { limit, listId, carousel = false }) {
    const response = await this.swish.api.items.list({
      limit,
      listId,
      sort: 'recently_saved',
    })

    const items = this.getResponseData(response, 'Unable to load saved items.')

    return this.renderSavedItemRecords(container, items, {
      carousel,
      showEmptyState: true,
    })
  }

  async renderSavedItemRecords (
    container,
    items,
    { carousel = false, showEmptyState = true },
  ) {
    if (items.length === 0) {
      this.destroyWishlistTriggersWithin(container)

      if (showEmptyState) {
        this.renderEmpty(container, this.strings.emptySavedItems)
      } else {
        container.replaceChildren()
      }

      return false
    }

    const products = await Promise.all(
      items.map(async (item) => {
        const { product } =
          await this.swish.storefront.loadResolvedProduct(item)

        return product
      }),
    )

    return this.renderSavedItems(container, products, {
      carousel,
      showEmptyState,
    })
  }

  async renderSavedItems (container, products, { carousel, showEmptyState }) {
    const validProducts = products.filter((product) => product?.handle)

    if (validProducts.length === 0) {
      this.destroyWishlistTriggersWithin(container)

      if (showEmptyState) {
        this.renderEmpty(container, this.strings.emptySavedProducts)
      } else {
        container.replaceChildren()
      }

      return false
    }

    const productCards = await Promise.all(
      validProducts.map((product) => {
        return this.getProductCardHtml(product.handle)
      }),
    )

    const validProductCards = productCards.filter(Boolean)

    if (validProductCards.length === 0) {
      this.destroyWishlistTriggersWithin(container)

      if (showEmptyState) {
        this.renderEmpty(container, this.strings.emptySavedProducts)
      } else {
        container.replaceChildren()
      }

      return false
    }

    this.destroyWishlistTriggersWithin(container)

    container.innerHTML = carousel
      ? this.getCarouselHtml(validProductCards)
      : validProductCards.join('')

    this.setupWishlistTriggersWithin(container)

    return true
  }

  /*
  |--------------------------------------------------------------------------
  | Carousels
  |--------------------------------------------------------------------------
  |
  */
  refreshCarouselsWithin (container) {
    const carousels = container.querySelectorAll('css-carousel')

    carousels.forEach((carousel) => {
      carousel.dispatchEvent(new CustomEvent('BAO::CSS_CAROUSEL::VISIBLE'))
    })
  }

  getCarouselHtml (productCards) {
    const slides = productCards
      .map((productCard) => {
        return `
          <div
            class="drw-Wishlist_Slide keen-slider__slide"
            data-css-carousel-el="slide"
          >
            ${productCard}
          </div>
        `
      })
      .join('')

    return `
      <css-carousel
        class="drw-Wishlist_Carousel"
        data-spacing="2"
        style="--Card_Spacing: 2px;"
      >
        <div
          class="drw-Wishlist_Slides keen-slider"
          data-css-carousel-el="slides"
        >
          ${slides}
        </div>
      </css-carousel>
    `
  }

  async getProductCardHtml (productHandle) {
    const cachedProductCard = this.productCardCache.get(productHandle)

    if (cachedProductCard) {
      return cachedProductCard
    }

    const productUrl = [
      window.Shopify?.routes?.root || '/',
      'products/',
      encodeURIComponent(productHandle),
    ].join('')

    try {
      const sectionHtml = await sectionRenderer.fetchSingle(
        PRODUCT_CARD_SECTION,
        productUrl,
      )

      const sectionDocument = sectionRenderer.convertToDOM(sectionHtml)
      const productCardElement = sectionDocument.querySelector(
        PRODUCT_CARD_SELECTOR,
      )

      if (!productCardElement) {
        console.error(
          `Swish Wishlist: No product card was returned for ${productHandle}.`,
        )

        return ''
      }

      const productCardHtml = productCardElement.outerHTML

      this.productCardCache.set(productHandle, productCardHtml)

      return productCardHtml
    } catch (error) {
      console.error(
        `Swish Wishlist: Failed to fetch product card for ${productHandle}.`,
        error,
      )

      return ''
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Lists
  |--------------------------------------------------------------------------
  |
  */
  async loadLists (
    container,
    { limit, renderLimit = limit, allowDelete = false },
  ) {
    const response = await this.swish.api.lists.list({
      limit,
      sort: 'recently_updated',
    })

    const lists = this.getResponseData(response, 'Unable to load lists.')

    if (lists.length === 0) {
      this.renderEmpty(container, this.strings.emptyLists)

      return lists
    }

    this.renderLists(container, lists.slice(0, renderLimit), { allowDelete })

    return lists
  }

  renderLists (container, lists, { allowDelete = false } = {}) {
    const fragment = document.createDocumentFragment()

    lists.forEach((list) => {
      const item =
        this.els.listTemplate.element.content.firstElementChild.cloneNode(true)

      this.populateListItem(item, list, { allowDelete })

      fragment.append(item)
    })

    container.replaceChildren(fragment)
  }

  populateListItem (item, list, { allowDelete = false } = {}) {
    const link = item.querySelector('[data-swish-list-el="link"]')
    const image = item.querySelector('[data-swish-list-el="image"]')
    const name = item.querySelector('[data-swish-list-el="name"]')
    const count = item.querySelector('[data-swish-list-el="count"]')
    const deleteTrigger = item.querySelector('[data-swish-list-el="delete"]')
    const deleteConfirm = item.querySelector(
      '[data-swish-list-el="deleteConfirm"]',
    )
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const previewImage = list.previewImages?.[0]
    const itemCount = list.itemCount || 0

    item.dataset.swishListId = list.id
    item.dataset.swishListName = list.name

    link.dataset.swishWishlistListId = list.id
    link.dataset.swishWishlistListName = list.name
    link.dataset.swishWishlistListCount = String(itemCount)

    const countTranslation =
      itemCount === 1 ? this.strings.listCountOne : this.strings.listCountOther

    name.textContent = list.name
    count.textContent = translateString(countTranslation, {
      count: itemCount,
    })

    if (previewImage?.url) {
      image.src = previewImage.url
      image.hidden = false
    }

    if (!deleteTrigger || !deleteConfirm || !deleteText) return

    deleteTrigger.hidden = !allowDelete
    deleteTrigger.setAttribute(
      'aria-label',
      translateString(this.strings.listDeleteLabel, {
        list: list.name,
      }),
    )
    deleteTrigger.setAttribute('aria-expanded', 'false')

    deleteText.textContent = translateString(this.strings.listDeleteConfirm, {
      list: list.name,
    })
  }

  handleListAction (trigger) {
    const item = trigger.closest('[data-swish-list-el="item"]')

    if (!item) return

    const action = trigger.dataset.swishListAction

    if (action === 'delete') {
      this.showListDeleteConfirm(item)

      return
    }

    if (action === 'cancel-delete') {
      this.hideListDeleteConfirm(item)

      return
    }

    if (action === 'confirm-delete') {
      this.deleteList(item)
    }
  }

  showListDeleteConfirm (item) {
    this.querySelectorAll('[data-delete-confirm="true"]').forEach(
      (listItem) => {
        if (listItem !== item) {
          this.hideListDeleteConfirm(listItem)
        }
      },
    )

    const deleteTrigger = item.querySelector('[data-swish-list-el="delete"]')
    const deleteConfirm = item.querySelector(
      '[data-swish-list-el="deleteConfirm"]',
    )
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const deleteError = item.querySelector(
      '[data-swish-list-el="deleteError"]',
    )
    const cancelTrigger = item.querySelector(
      '[data-swish-list-action="cancel-delete"]',
    )

    if (!deleteConfirm) return

    item.dataset.deleteConfirm = 'true'
    deleteConfirm.hidden = false
    deleteTrigger?.setAttribute('aria-expanded', 'true')

    if (deleteText) {
      deleteText.hidden = false
    }

    if (deleteError) {
      deleteError.textContent = ''
      deleteError.hidden = true
    }

    requestAnimationFrame(() => {
      cancelTrigger?.focus()
    })
  }

  hideListDeleteConfirm (item) {
    const deleteTrigger = item.querySelector('[data-swish-list-el="delete"]')
    const deleteConfirm = item.querySelector(
      '[data-swish-list-el="deleteConfirm"]',
    )
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const deleteError = item.querySelector(
      '[data-swish-list-el="deleteError"]',
    )

    delete item.dataset.deleteConfirm
    delete item.dataset.loading

    if (deleteConfirm) {
      deleteConfirm.hidden = true
    }

    deleteTrigger?.setAttribute('aria-expanded', 'false')

    if (deleteText) {
      deleteText.hidden = false
    }

    if (deleteError) {
      deleteError.textContent = ''
      deleteError.hidden = true
    }
  }

  async deleteList (item) {
    const listId = item.dataset.swishListId
    const listName = item.dataset.swishListName
    const deleteText = item.querySelector('[data-swish-list-el="deleteText"]')
    const deleteError = item.querySelector(
      '[data-swish-list-el="deleteError"]',
    )
    const actionTriggers = item.querySelectorAll('[data-swish-list-action]')
    const listApi = this.swish.api?.lists || this.swish.lists
    const deleteById = listApi?.deleteById

    if (!listId || item.dataset.loading === 'true') return

    if (typeof deleteById !== 'function') {
      console.error('Swish Wishlist: List deletion is unavailable.')

      if (deleteText) {
        deleteText.hidden = true
      }

      if (deleteError) {
        deleteError.textContent = this.strings.listDeleteError
        deleteError.hidden = false
      }

      return
    }

    item.dataset.loading = 'true'
    item.setAttribute('aria-busy', 'true')

    actionTriggers.forEach((actionTrigger) => {
      actionTrigger.disabled = true
    })

    if (deleteText) {
      deleteText.hidden = false
    }

    if (deleteError) {
      deleteError.textContent = ''
      deleteError.hidden = true
    }

    try {
      const response = await deleteById.call(listApi, listId)

      if (response?.error) {
        throw new Error(
          response.error?.message || `Unable to delete ${listName}.`,
        )
      }

      item.remove()

      if (
        !this.els.listsContent.element.querySelector(
          '[data-swish-list-el="item"]',
        )
      ) {
        this.renderEmpty(
          this.els.listsContent.element,
          this.strings.emptyLists,
        )
      }
    } catch (error) {
      console.error('Swish Wishlist: Unable to delete the list.', error)

      item.dataset.loading = 'false'
      item.setAttribute('aria-busy', 'false')

      actionTriggers.forEach((actionTrigger) => {
        actionTrigger.disabled = false
      })

      if (deleteText) {
        deleteText.hidden = true
      }

      if (deleteError) {
        deleteError.textContent = this.strings.listDeleteError
        deleteError.hidden = false
      }
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Swish
  |--------------------------------------------------------------------------
  |
  */
  async getSwish () {
    if (window.swish) {
      return window.swish
    }

    if (swishPromise) {
      return swishPromise
    }

    swishPromise = new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        document.removeEventListener(SWISH_READY_EVENT, handleReady)

        swishPromise = null

        reject(new Error('Swish did not initialise within 10 seconds.'))
      }, SWISH_READY_TIMEOUT)

      function handleReady () {
        window.clearTimeout(timeout)

        if (!window.swish) {
          swishPromise = null

          reject(
            new Error(
              'Swish fired its ready event but window.swish is unavailable.',
            ),
          )

          return
        }

        resolve(window.swish)
      }

      document.addEventListener(SWISH_READY_EVENT, handleReady, {
        once: true,
      })
    })

    return swishPromise
  }

  getResponseData (response, fallbackMessage) {
    if ('error' in response) {
      throw new Error(response.error?.message || fallbackMessage)
    }

    return response.data ?? []
  }

  get toast () {
    return this.getExternalElements('toast')[0] || null
  }

  get strings () {
    return window.theme.strings.wishlist
  }

  /*
  |--------------------------------------------------------------------------
  | Elements
  |--------------------------------------------------------------------------
  |
  */
  getExternalElements (name) {
    const externalElement = this.externalEls[name]

    if (!externalElement) {
      return []
    }

    if (externalElement.elements) {
      return Array.from(externalElement.elements)
    }

    if (externalElement.element) {
      return [externalElement.element]
    }

    return []
  }

  /*
  |--------------------------------------------------------------------------
  | Empty and error states
  |--------------------------------------------------------------------------
  |
  */
  renderEmpty (container, text) {
    const message = document.createElement('p')

    message.className = 'drw-Drawer_Empty fz-15_150'
    message.textContent = text

    this.destroyWishlistTriggersWithin(container)

    container.replaceChildren(message)
  }

  renderError (container, text) {
    const message = document.createElement('p')

    message.className = 'drw-Drawer_Error'
    message.setAttribute('role', 'alert')
    message.textContent = text

    this.destroyWishlistTriggersWithin(container)

    container.replaceChildren(message)
  }

  renderViewError (view) {
    const containers = {
      [VIEWS.HOME]: this.els.homeSaves.element,
      [VIEWS.SAVES]: this.els.savesContent.element,
      [VIEWS.LISTS]: this.els.listsContent.element,
      [VIEWS.LIST]: this.els.listContent.element,
    }

    const container = containers[view]

    if (!container) return

    this.renderError(container, this.strings.errorView)
  }
}

if (!customElements.get('swish-wishlist')) {
  customElements.define('swish-wishlist', SwishWishlist)
}
